import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Feather, Flower2 } from "lucide-react";

/**
 * ThemedWeekBanner — a quiet weekly theme card mounted on Rituals + Journal.
 *
 * The theme rotates by ISO week (server-side), so every member sees the
 * same word in the same week. It's not a task or a challenge — it's an
 * atmosphere. Each theme carries one soft invitation (for Rituals) and one
 * opening prompt (for Journal); the ``variant`` prop chooses which one to
 * emphasize on this page.
 */
const HUE_TOKENS = {
  clay:       { pill: "hsl(15 22% 46%)",  wash: "hsl(15 22% 46% / 0.08)", ring: "hsl(15 22% 46% / 0.35)" },
  moss:       { pill: "hsl(96 10% 39%)",  wash: "hsl(96 10% 39% / 0.08)", ring: "hsl(96 10% 39% / 0.35)" },
  terracotta: { pill: "hsl(24 51% 63%)",  wash: "hsl(24 51% 63% / 0.10)", ring: "hsl(24 51% 63% / 0.40)" },
};

export default function ThemedWeekBanner({ variant = "rituals" }) {
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await endpoints.themedWeekCurrent();
        if (alive) setTheme(data);
      } catch { /* silent — a missing banner is quieter than an error */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!theme) return null;

  const tokens = HUE_TOKENS[theme.hue] || HUE_TOKENS.clay;
  const line = variant === "journal" ? theme.journal_prompt : theme.ritual_invitation;
  const Icon = variant === "journal" ? Feather : Flower2;

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline px-5 py-4 md:px-6 md:py-5"
      style={{ background: tokens.wash, borderColor: tokens.ring }}
      data-testid={`themed-week-banner-${variant}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full"
          style={{ background: tokens.pill, color: "hsl(var(--paper))" }}
        >
          <Icon size={11} /> This week
        </span>
        <span
          className="font-display italic text-2xl md:text-3xl leading-none"
          style={{ color: tokens.pill }}
          data-testid="themed-week-name"
        >
          {theme.name}
        </span>
      </div>
      <p
        className="mt-2 font-display italic text-base md:text-lg text-[hsl(var(--fg))]/85 leading-snug"
        data-testid="themed-week-essence"
      >
        {theme.essence}
      </p>
      {line && (
        <p
          className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl"
          data-testid="themed-week-line"
        >
          <span className="text-[10px] uppercase tracking-[0.22em] mr-2" style={{ color: tokens.pill }}>
            {variant === "journal" ? "A doorway" : "An invitation"}
          </span>
          {line}
        </p>
      )}
    </section>
  );
}
